/**
 * RippleLink — Link wrapper with click ripple + 200ms navigation delay.
 *
 * Iter. 331: forwardRef so parents can measure DOM (used by the bottom-bar
 * pill math centering).
 */
import React, { forwardRef, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RIPPLE_DURATION = 600;
const NAVIGATE_DELAY = 200;

const RippleLink = forwardRef(({ to, onClick, className = '', style, children, ...rest }, ref) => {
  const navigate = useNavigate();
  const [ripples, setRipples] = useState([]);
  const idRef = useRef(0);

  const handleClick = useCallback((e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;
    const id = ++idRef.current;
    setRipples(rs => [...rs, { id, x, y, size }]);
    setTimeout(() => setRipples(rs => rs.filter(r => r.id !== id)), RIPPLE_DURATION);

    if (typeof onClick === 'function') onClick(e);

    setTimeout(() => {
      if (to) navigate(to);
    }, NAVIGATE_DELAY);
  }, [navigate, onClick, to]);

  return (
    <a
      ref={ref}
      href={to || '#'}
      onClick={handleClick}
      className={`relative ${className}`}
      style={style}
      {...rest}
    >
      <span
        aria-hidden="true"
        className="absolute inset-0 overflow-hidden rounded-[inherit] pointer-events-none"
      >
        {ripples.map(r => (
          <span
            key={r.id}
            className="elite-ripple"
            style={{ left: r.x, top: r.y, width: r.size, height: r.size }}
          />
        ))}
      </span>
      {children}
    </a>
  );
});

RippleLink.displayName = 'RippleLink';

export default RippleLink;
